<template>
    <div>
      <InventoryHeaderTab />
  
      <!-- Page Header (screen only) -->
      <div class="page-header no-print">
        <div>
          <h5 class="page-title">Print Stock Transfer</h5>
          <p class="page-sub">
            <span class="batch-pill">{{ store_transfer?.batch_code }}</span>
          </p>
        </div>
        <div class="page-actions">
          <router-link :to="$url('/all-stock-transfer')" class="btn-outline">
            <font-awesome-icon icon="arrow-left" /> Back
          </router-link>
          <PrintButtonHeader
            :title="'Invoice-' + store_transfer?.batch_code"
            :urlA4="'/view-stock-transfer/'"
            :url58mm="'/print58mm-stock-transfer/'"
            :url80mm="'/print80mm-stock-transfer/'"
            :currentUrl="'Print 80mm Stock Transfer'"
            :data="store_transfer"
            :type="'Stock Transfer'"
          />
          <button class="btn-primary-action no-print" @click="printPage('a4')">
            <font-awesome-icon icon="print" /> Print
          </button>
        </div>
      </div>
  
      <!-- Loading -->
      <div v-if="loading" class="form-card no-print">
        <div class="skeleton-grid">
          <div class="skeleton skeleton--field" v-for="n in 4" :key="n"></div>
        </div>
      </div>
  
      <!-- Invoice Preview -->
      <div v-else class="invoice-wrap">
        <div class="invoice" id="printMe">
  
          <!-- Invoice Header -->
          <div class="invoice-header">
            <div class="invoice-brand">
              <div>
                <p class="invoice-brand-name">Stock Transfer</p>
                <p class="invoice-brand-sub">Powered by Mapiibs</p>
              </div>
            </div>
            <div class="invoice-meta">
              <p class="invoice-batch">{{ store_transfer?.batch_code }}</p>
              <p class="invoice-date">{{ dateTime(store_transfer?.date) }}, {{ timeConvert(store_transfer?.time) }}</p>
            </div>
          </div>
  
          <div class="invoice-divider"></div>
  
          <!-- Transfer Info -->
          <div class="invoice-info-grid">
            <div class="invoice-info-item">
              <span class="invoice-info-label">Source Store</span>
              <span class="invoice-info-value">{{ store_transfer?.store?.store_name }}</span>
            </div>
            <div class="invoice-info-item invoice-info-item--arrow">
              <font-awesome-icon icon="arrow-right" class="flow-arrow" />
            </div>
            <div class="invoice-info-item">
              <span class="invoice-info-label">Destination Store</span>
              <span class="invoice-info-value">{{ store_transfer?.destination?.store_name }}</span>
            </div>
            <div class="invoice-info-item">
              <span class="invoice-info-label">Transferred By</span>
              <span class="invoice-info-value">{{ store_transfer?.user?.fullname }}</span>
            </div>
          </div>
  
          <div class="invoice-divider invoice-divider--dashed"></div>
  
          <!-- Items Table -->
          <table class="invoice-table">
            <thead>
              <tr>
                <th>#</th>
                <th class="col-barcode">Barcode</th>
                <th>Product</th>
                <th class="col-uom">Variant / UOM</th>
                <th class="text-right">Qty</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(item, index) in issue_stock_item" :key="item.id">
                <td class="text-muted">{{ index + 1 }}</td>
                <td class="text-muted mono col-barcode">{{ item?.product?.bar_code || '—' }}</td>
                <td class="fw-bold">{{ item?.product?.product_name }}</td>
                <td class="text-muted col-uom">{{ item?.variant?.variant_name ?? item?.product?.unit_of_measurement }}</td>
                <td class="text-right fw-bold">{{ item?.quantity }}</td>
              </tr>
            </tbody>
          </table>
  
          <div class="invoice-divider invoice-divider--dashed"></div>
  
          <!-- Footer -->
          <div class="invoice-footer">
            <p class="invoice-footer-note">Transfer No: <strong>{{ store_transfer?.batch_code }}</strong></p>
            <p class="invoice-footer-brand">Powered by Mapiibs</p>
          </div>
  
        </div>
      </div>
    </div>
  </template>
  
  <script>
  import InventoryHeaderTab from './component/inventoryheadertab.vue';
  import axios from 'axios';
  import { reusableMixin } from '../../../lib/reusableMixin';
  
  export default {
    components: { InventoryHeaderTab },
    mixins: [reusableMixin],
  
    data() {
      return {
        store_transfer:   null,
        issue_stock_item: [],
        loading:          false,
      };
    },
  
    created() { this.getStockTransfer(); },
  
    methods: {
      printPage(width) {
        const isNarrow = /^\d+mm$/.test(width) && parseInt(width) <= 80;
        const margin   = isNarrow ? '4mm' : '12mm';

        const style = document.createElement('style');
        style.id    = '__print-receipt-override';
        style.innerHTML = `
          @page { size: ${width} auto; margin: ${margin}; }
          @media print {
            body > * { visibility: hidden !important; }
            #printMe, #printMe * { visibility: visible !important; }
            #printMe {
              position: absolute;
              left: 0; top: 0;
              width: 100%;
              background: white;
              padding: ${isNarrow ? '6px' : '20px'};
              box-sizing: border-box;
            }
            ${isNarrow ? '.col-barcode, .col-uom { display: none !important; }' : ''}
          }
        `;
        document.head.appendChild(style);
        window.print();
        setTimeout(() => document.getElementById('__print-receipt-override')?.remove(), 1000);
      },

      getStockTransfer() {
        this.loading = true;
        axios.get(`/inventory/issue-stock/${this.$route.params.id}`)
          .then(response => {
            this.loading          = false;
            this.store_transfer   = response.data.data;
            this.issue_stock_item = response.data.data.issue_stock_item;
          })
          .catch(() => {
            this.loading = false;
            this.$toast?.error('Something went wrong');
          });
      },
    },
  };
  </script>
  
  <style scoped>
  /* ── Screen: Page Header ── */
  .page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
  .page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 6px; }
  .page-sub     { margin:0; }
  .page-actions { display:flex; align-items:center; gap:8px; flex-wrap:wrap; }
  
  /* ── Skeleton ── */
  .skeleton-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:16px; }
  .skeleton      { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
  @keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
  .skeleton--field { height:52px; }
  
  /* ── Invoice Wrap (screen) ── */
  .invoice-wrap {
    display:flex; justify-content:center; padding: 0 0 32px;
  }
  
  .invoice {
    background:#fff; border:1px solid #e4eaf2; border-radius:12px;
    padding:40px 48px; width:100%; max-width:720px;
    box-shadow:0 4px 24px rgba(0,0,0,.06);
  }
  
  /* ── Invoice Header ── */
  .invoice-header { display:flex; align-items:flex-start; justify-content:space-between; gap:16px; margin-bottom:24px; }
  .invoice-brand  { display:flex; align-items:center; gap:14px; }
.invoice-brand-name { font-size:18px; font-weight:800; color:#1a2b45; margin:0 0 2px; }
  .invoice-brand-sub  { font-size:12px; color:#7a90b0; margin:0; }
  
  .invoice-meta  { text-align:right; }
  .invoice-batch { font-family:monospace; font-size:15px; font-weight:700; color:#1a2b45; background:#f4f7fb; border:1px solid #e4eaf2; border-radius:6px; padding:4px 12px; display:inline-block; margin:0 0 6px; }
  .invoice-date  { font-size:12.5px; color:#7a90b0; margin:0; }
  
  /* ── Divider ── */
  .invoice-divider        { height:1px; background:#e4eaf2; margin:20px 0; }
  .invoice-divider--dashed { border:none; border-top:2px dashed #e4eaf2; background:transparent; }
  
  /* ── Info Grid ── */
  .invoice-info-grid { display:flex; align-items:center; gap:12px; flex-wrap:wrap; }
  .invoice-info-item { display:flex; flex-direction:column; gap:4px; flex:1; min-width:140px; }
  .invoice-info-item--arrow { flex:0 0 auto; min-width:auto; }
  .invoice-info-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; }
  .invoice-info-value { font-size:14px; font-weight:700; color:#1a2b45; }
  .flow-arrow { font-size:18px; color:#aab8cc; }
  
  /* ── Invoice Table ── */
  .invoice-table { width:100%; border-collapse:collapse; }
  .invoice-table thead tr { border-bottom:2px solid #e4eaf2; }
  .invoice-table thead th { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.05em; color:#7a90b0; padding:8px 10px; text-align:left; }
  .invoice-table tbody tr { border-bottom:1px solid #f0f4f8; }
  .invoice-table tbody tr:last-child { border-bottom:none; }
  .invoice-table tbody td { padding:10px; font-size:13.5px; vertical-align:middle; color:#1a2b45; }
  .text-right { text-align:right; }
  .text-muted  { color:#4a5e78; }
  .fw-bold     { font-weight:700; }
  .mono        { font-family:monospace; font-size:13px; }
  
  /* ── Invoice Footer ── */
  .invoice-footer { display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:8px; }
  .invoice-footer-note  { font-size:13px; color:#4a5e78; margin:0; }
  .invoice-footer-brand { font-size:12px; color:#aab8cc; margin:0; }
  
  /* ── Buttons ── */
  .batch-pill { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
  .btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:9px 18px; border-radius:8px; border:none; cursor:pointer; transition:background .15s; }
  .btn-primary-action:hover { background:#003b8e; }
  .btn-outline { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:9px 18px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; }
  .btn-outline:hover { background:#f4f7fb; color:#1a2b45; }
  
  /* ── Responsive ── */
  @media(max-width:768px) {
    .invoice { padding:24px 20px; }
    .invoice-header { flex-direction:column; }
    .invoice-meta { text-align:left; }
    .page-header,.page-actions { flex-direction:column; align-items:stretch; }
    .btn-outline,.btn-primary-action { width:100%; justify-content:center; }
  }
  </style>